import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

const BarcodeScanner = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState([]);
  const [activeCamera, setActiveCamera] = useState(null);

  useEffect(() => {
    const scannerId = 'barcode-scanner-region';
    const html5QrCode = new Html5Qrcode(scannerId);
    html5QrCodeRef.current = html5QrCode;

    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices.length === 0) {
          setError('No cameras found on this device.');
          return;
        }
        setCameras(devices);
        const backCamera = devices.find(
          (d) => d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('rear')
        );
        const cameraId = backCamera ? backCamera.id : devices[devices.length - 1].id;
        setActiveCamera(cameraId);
        startScanner(html5QrCode, cameraId);
      })
      .catch(() => {
        setError('Camera permission denied. Please allow camera access and try again.');
      });

    return () => {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScanner = (scanner, cameraId) => {
    scanner
      .start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: 280, height: 150 },
          aspectRatio: 1.0,
          formatsToSupport: [
            0, // QR_CODE
            1, // AZTEC
            2, // CODABAR
            3, // CODE_39
            4, // CODE_93
            5, // CODE_128
            6, // DATA_MATRIX
            7, // MAXICODE
            8, // ITF
            9, // EAN_13
            10, // EAN_8
            11, // PDF_417
            12, // RSS_14
            13, // RSS_EXPANDED
            14, // UPC_A
            15, // UPC_E
            16, // UPC_EAN_EXTENSION
          ],
        },
        (decodedText) => {
          scanner.stop().then(() => {
            onScan(decodedText);
          }).catch(() => {
            onScan(decodedText);
          });
        },
        () => {}
      )
      .catch((err) => {
        setError(`Failed to start camera: ${err}`);
      });
  };

  const switchCamera = (cameraId) => {
    const scanner = html5QrCodeRef.current;
    if (!scanner) return;
    setActiveCamera(cameraId);
    if (scanner.isScanning) {
      scanner.stop().then(() => startScanner(scanner, cameraId)).catch(() => {});
    } else {
      startScanner(scanner, cameraId);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'env(safe-area-inset-top) 16px calc(16px + env(safe-area-inset-bottom))',
      }}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: '20px',
          width: '100%',
          maxWidth: '400px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>
            Scan Barcode
          </h3>
          <button onClick={onClose} className="btn btn-ghost btn-sm" aria-label="Close scanner">
            &times;
          </button>
        </div>

        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: '1.5' }}>
          Point your camera at a product barcode (UPC, EAN, etc.)
        </p>

        {error && (
          <div style={{
            background: 'rgba(220,53,69,0.08)',
            border: '1px solid rgba(220,53,69,0.2)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 14px',
            marginBottom: '12px',
            fontSize: '13px',
            color: 'var(--color-danger)',
          }}>
            {error}
          </div>
        )}

        <div
          id="barcode-scanner-region"
          ref={scannerRef}
          style={{ width: '100%', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginBottom: '12px' }}
        />

        {cameras.length > 1 && (
          <div style={{ marginBottom: '12px' }}>
            <label className="form-label" style={{ marginBottom: '4px' }}>Camera</label>
            <select
              className="form-select"
              style={{ width: '100%' }}
              value={activeCamera || ''}
              onChange={(e) => switchCamera(e.target.value)}
            >
              {cameras.map((cam) => (
                <option key={cam.id} value={cam.id}>
                  {cam.label || `Camera ${cam.id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        <button onClick={onClose} className="btn btn-secondary btn-block">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default BarcodeScanner;
